import {observer} from 'mobx-react-lite'
import React, {useCallback} from 'react'
import {Lang} from 'yougile-lang'
import {addingAllowed} from '@client/hub-actions-allowed'
import {Sync} from '@client/sync'
import {AddTask} from '@components/add-task'
import {useBoardStore} from '@components/board/board-store'
import {ColumnMenu} from '@components/columns/column-menu'
import {
  getGroupLocationPath,
  useColumnStore
} from '@components/columns/column-store'
import {
  TwTaskGroupTitle,
  TwTitleRow
} from '@components/columns/column-title/containers'
import {EditableColumnTitle} from '@components/columns/column-title/editable-column-title'
import {MultiActionsCheckbox} from '@components/columns/column-title/multi-actions-checkbox'
import {MirrorColumnPath} from '@components/columns/mirror-column/mirror-column-path'
import {Icon} from '@components/common/base/icon'
import {DnDDataObject, useDnDStore} from '@components/draggable'
import {WithDroppable} from '@components/droppable'
import {useSyncEventUpdateEffect} from '@containers/hooks'

export const MirrorColumnHeader = observer(() => {
  const [, dndStore] = useDnDStore()
  const [, boardStore] = useBoardStore()
  const [, store] = useColumnStore()
  const box = Sync.getCurrentSandbox()

  const columnId = store.getVisibleColumnId()
  const column = box.getHub(columnId)
  const columnPath = getGroupLocationPath(column)

  // force update when any of path item names is changed to update path items
  useSyncEventUpdateEffect('current', {
    filter: columnPath.map(x => x.id),
    deps: [columnId]
  })

  // mirror column header displays current column's path&add form that must also act as a subtask dropzone
  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      store.onDrop(e)
      column && store.onSubtaskColumnDrop(column)
    },
    [store, column]
  )

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      store.onDragOver(e)
      store.onSubtasksColumnDragOver(e, column?.id)
    },
    [store, column]
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      store.onDragLeave(e)
      store.onSubtasksColumnDragLeave(column?.id)
    },
    [store, column]
  )

  const customDnDDragEnter = useCallback(
    () =>
      columnId &&
      store.setCustomPlaceholderForMirrorColumn(
        store.column.id,
        columnId,
        dndStore.state.draggableRect?.height ?? 0
      ),
    [store, columnId, dndStore]
  )

  const customDnDDrop = useCallback(
    (drag: DnDDataObject) => {
      store.clearCustomPlaceholderForMirrorColumn()
      column &&
        boardStore.onTaskDrop({
          drag,
          toColumn: column,
          columnStore: store
        })
    },
    [column, boardStore, store]
  )

  return (
    // A mirror column title is also a dropzone for the scrolled to mirrored column
    <WithDroppable
      accepts={['task']}
      showPlaceholder={false}
      onDragEnter={customDnDDragEnter}
      onDragLeave={store.clearCustomPlaceholderForMirrorColumn}
      onDrop={customDnDDrop}
      container=".task-group-title-new"
    >
      <TwTaskGroupTitle
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="px-8 pb-4"
      >
        <TwTitleRow className="text-secondary uppercase font-semibold text-10">
          {Lang.msg.mirrorColumn}
        </TwTitleRow>
        <TwTitleRow className="pr-4">
          <MultiActionsCheckbox columnId={store.column.id} />
          <Icon icon="IconCustomMirror" size={16} className="!h-24" />
          <EditableColumnTitle type="mirror" />
          <ColumnMenu className="flex-none -mr-8" />
        </TwTitleRow>
        {!!columnPath.length && <MirrorColumnPath items={columnPath} />}
        {!!columnId && addingAllowed(columnId) && (
          <TwTitleRow className="!block">
            <AddTask columnId={columnId} scrollTo="task-top" />
          </TwTitleRow>
        )}
      </TwTaskGroupTitle>
    </WithDroppable>
  )
})
